import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import Button from '@mui/material/Button';
import Add from '@mui/icons-material/Add';
import AddCircle from '@mui/icons-material/AddCircle';
import CloudUpload from '@mui/icons-material/CloudUpload';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import DragIndicator from '@mui/icons-material/DragIndicator';
import Grid from '@mui/material/Grid2';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { PageLoader } from '../../shared/PageLoader';
import { ColorSquare } from '../../Analysis/DeckPlan/ColorLegend/ColorLegend';
import { EmptyStatePlaceholder } from '@/components/Analysis/shared/EmptyStatePlaceholder';
import { DropDownField } from '@/components/shared/CustomField';
import { TemplateFields } from '../TemplateForm/shared';
import { BulkOptionsModal } from '../TemplateForm/BulkOptionsModal';
import { GenericDialog } from '../../shared/GenericDialog';
import {
  DropDownContainer,
  dropDownStyles,
  Field,
  FieldPanel,
  FieldsList,
  FormHeader,
  LineBreak,
  OptionBox,
  TemplateFieldContainer,
  tooltipStyles,
} from '../TemplateForm/styles';
import { containedButtonStyle } from '@/theme';
import { abyssColors } from '@/theme/colors';
import {
  selectedWorklogsTemplateId,
  worklogsTemplateFields,
  selectedWorklogsTemplateField,
  selectedWorklogsTemplate,
  selectedCostLogsTemplateId,
  costLogsTemplateFields,
  selectedCostLogsTemplateField,
  selectedCostLogsTemplate,
} from '../state';
import { formatEnumForDisplay, upperSnakeCase } from '@/utils';
import { removeTypename, useSnackBarMessage } from '../utils';
import { generateMongoObjectId } from '@/utils/databaseHelpers';
import { NEW_FIELD_PREFIX } from '../constants';
import {
  AllLogsTemplatesByTypeDocument,
  GetVersionsDocument,
  LogTemplateType,
  Maybe,
  SectionUnitsEnum,
  SortTypeEnum,
  TableCellStyleEnum,
  TemplateFieldTypeEnum,
  TextFormatEnum,
  useUpdateLogsTemplateMutation,
  LogsTemplateDocument,
  LogsTemplateField,
} from '@/__generated__/graphql';

type SortableFieldProps = {
  field: LogsTemplateField;
  isSelected: boolean;
  isDragDisabled: boolean;
  onClick: () => void;
};

const SortableField = ({ field, isSelected, isDragDisabled, onClick }: SortableFieldProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.id,
    disabled: isDragDisabled,
  });

  return (
    <Field
      ref={setNodeRef}
      selected={isSelected}
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
    >
      {!isDragDisabled && (
        <Box
          {...attributes}
          {...listeners}
          sx={{
            cursor: 'grab',
            display: 'flex',
            alignItems: 'center',
            color: 'text.secondary',
            flexShrink: 0,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <DragIndicator fontSize="small" />
        </Box>
      )}
      {field.name}
    </Field>
  );
};

type SharedLogsFieldsPanelProps = {
  structureId: string;
  emptyForm: boolean;
  handleEmptyForm: (value: boolean) => void;
  hostLogTemplateType: LogTemplateType;
};

export const SharedLogsFieldsPanel = ({
  structureId,
  emptyForm,
  handleEmptyForm,
  hostLogTemplateType,
}: SharedLogsFieldsPanelProps) => {
  const generateFieldKeyFromName = useCallback((name: string) => {
    const normalized = name
      .trim()
      .toLowerCase()
      .replaceAll(/[^a-z0-9]+/g, '_')
      .replaceAll(/^_+|_+$/g, '')
      .replaceAll(/_+/g, '_');
    return normalized;
  }, []);

  const [mode, setMode] = useState<'ReadOnly' | 'Edit'>('ReadOnly');
  const worklogsTemplateId = useAtomValue(selectedWorklogsTemplateId);
  const costLogsTemplateId = useAtomValue(selectedCostLogsTemplateId);

  const hostTemplateId =
    hostLogTemplateType === LogTemplateType.CostLogTemplate
      ? costLogsTemplateId
      : worklogsTemplateId;

  const worklogsTemplate = useAtomValue(selectedWorklogsTemplate);
  const costLogsTemplate = useAtomValue(selectedCostLogsTemplate);

  const hostTemplate =
    hostLogTemplateType === LogTemplateType.CostLogTemplate ? costLogsTemplate : worklogsTemplate;

  const [allTemplateFields, setAllTemplateFields] = useAtom(
    hostLogTemplateType === LogTemplateType.CostLogTemplate
      ? costLogsTemplateFields
      : worklogsTemplateFields
  );

  const [selectedField, setSelectedField] = useAtom(
    hostLogTemplateType === LogTemplateType.CostLogTemplate
      ? selectedCostLogsTemplateField
      : selectedWorklogsTemplateField
  );
  const [newOption, setNewOption] = useState<string>();
  const [newOptionColor, setNewOptionColor] = useState<string>();
  const [searchTerm, setSearchTerm] = useState('');
  const initialSelectedFieldRef = useRef<LogsTemplateField | undefined>(undefined);
  const userSelectedFieldRef = useRef<LogsTemplateField | undefined>(undefined);
  const [showOptionsModal, setShowOptionsModal] = useState<boolean>(false);
  const [showDeleteFieldDialog, setShowDeleteFieldDialog] = useState<boolean>(false);
  const [enableColors, setEnableColors] = useState<boolean>(!!selectedField?.displayConfig?.color);

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const isEditMode = mode === 'Edit' || emptyForm;
  const isDragDisabled = !isEditMode || !!searchTerm;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      setAllTemplateFields((previous) => {
        if (!previous) return undefined;
        const oldIndex = previous.findIndex((field) => field.id === active.id);
        const newIndex = previous.findIndex((field) => field.id === over.id);
        if (oldIndex === -1 || newIndex === -1) return previous;
        return arrayMove(previous, oldIndex, newIndex);
      });
    },
    [setAllTemplateFields]
  );

  // Get available equations for derived fields
  const availableEquations = useMemo(() => {
    const equations = hostTemplate?.equations?.filter((equation) => equation !== null) || [];
    return equations.map((equation) => ({
      key: equation.key,
      name: equation.name || equation.key,
    }));
  }, [hostTemplate?.equations]);

  useEffect(() => {
    if (initialSelectedFieldRef.current === undefined && selectedField) {
      initialSelectedFieldRef.current = selectedField;
    }
  }, [selectedField]);

  const filteredFields = useMemo(() => {
    if (!allTemplateFields) return [];
    const q = searchTerm.trim().toLowerCase();
    return allTemplateFields.filter((field) =>
      q ? (field.name ?? '').toLowerCase().includes(q) : true
    );
  }, [allTemplateFields, searchTerm]);

  useEffect(() => {
    const q = searchTerm.trim().toLowerCase();

    const setSelection = (field: LogsTemplateField | undefined) => {
      if (!field) return;
      const normalized = field.displayConfig ? field : { ...field, displayConfig: undefined };
      setSelectedField(normalized);

      const colorValue = normalized.displayConfig?.color;
      setEnableColors(!!colorValue && (Array.isArray(colorValue) ? colorValue.length > 0 : true));
    };

    if (!q) {
      setSelection(userSelectedFieldRef.current ?? initialSelectedFieldRef.current);
      return;
    }

    if (filteredFields.length > 0) {
      const currentSelectedId = selectedField?.id;
      const currentFieldInFiltered = filteredFields.find((field) => field.id === currentSelectedId);

      if (currentFieldInFiltered) {
        return;
      }

      const first = filteredFields[0];
      setSelection(first);
    }
  }, [searchTerm, filteredFields, setSelectedField, setEnableColors, selectedField?.id]);

  const updateField = useCallback(
    (field: LogsTemplateField | undefined) => {
      if (!field) {
        return;
      }
      setSelectedField(field);
      userSelectedFieldRef.current = field;
      setAllTemplateFields((previous) => {
        if (!previous) return undefined;
        return previous.map((logsTemplateField) => {
          if (logsTemplateField.id === field.id) return field;
          return logsTemplateField;
        });
      });
    },
    [setAllTemplateFields, setSelectedField]
  );

  const handleDeleteField = () => {
    if (selectedField) {
      const deletedFieldId = selectedField.id;

      setAllTemplateFields((previous) => {
        if (!previous) return undefined;

        const updatedFields = previous.filter(
          (logsTemplateField) => logsTemplateField.id !== deletedFieldId
        );

        if (updatedFields.length > 0) {
          const nextField = updatedFields[0];
          setTimeout(() => {
            setSelectedField(
              nextField.displayConfig ? nextField : { ...nextField, displayConfig: undefined }
            );
            userSelectedFieldRef.current = nextField;
          }, 0);
        } else {
          setSelectedField(undefined);
          userSelectedFieldRef.current = undefined;
        }

        return updatedFields;
      });

      if (initialSelectedFieldRef.current?.id === deletedFieldId) {
        initialSelectedFieldRef.current = undefined;
      }

      setShowDeleteFieldDialog(false);
    }
  };

  const handleRemoveOption = (option: string) => {
    if (selectedField) {
      const options = selectedField?.options?.filter((item) => item !== option) ?? [];
      updateField({ ...selectedField, options });
    }
  };

  const [updateTemplateField] = useUpdateLogsTemplateMutation({
    refetchQueries: [AllLogsTemplatesByTypeDocument, LogsTemplateDocument],
    onCompleted: (response) => {
      if (!response?.updateLogsTemplate) {
        showSnackBar('An error occurred while updating template', 'error');
        return;
      }
      showSnackBar('Template saved successfully', 'success');
    },
    onError: (error) => {
      console.error(error);
      showSnackBar(error.message, 'error');
    },
  });

  const handleSaveTemplate = useCallback(() => {
    if (selectedField) {
      const uniqueNewFields: LogsTemplateField[] = [];
      const duplicateNewFields: LogsTemplateField[] = [];

      const existingKeys = new Set<Maybe<string> | undefined>();
      const existingNames = new Set<string>();

      const cleanField = (field: LogsTemplateField) => {
        const cleaned = removeTypename(field);

        // Remove null values
        Object.keys(cleaned).forEach((key) => {
          if (cleaned[key as keyof typeof cleaned] === null) {
            delete cleaned[key as keyof typeof cleaned];
          }
        });

        return cleaned;
      };

      for (const field of allTemplateFields ?? []) {
        const isNewField = field.id.startsWith(NEW_FIELD_PREFIX);

        if (!isNewField) {
          existingKeys.add(field.key);
          existingNames.add(field.name);
          continue;
        }

        const isDuplicate = existingKeys.has(field.key) || existingNames.has(field.name);
        (isDuplicate ? duplicateNewFields : uniqueNewFields).push(field);
      }

      if (duplicateNewFields.length > 0) {
        showSnackBar('Remove duplicate fields to save template.', 'error');
        return;
      }

      // Validate derived fields have equation keys
      const invalidDerivedFields = allTemplateFields?.filter(
        (field) => field.isDerived && !field.derivedEquationKey
      );

      if (invalidDerivedFields && invalidDerivedFields.length > 0) {
        showSnackBar('All derived fields must have an equation key specified.', 'error');
        return;
      }

      updateTemplateField({
        variables: {
          input: {
            structureId: structureId,
            templateId: hostTemplateId ?? '',
            fields: (allTemplateFields ?? []).map(cleanField),
            lastModifiedFor: 'Update Template Fields',
          },
        },
        refetchQueries: [GetVersionsDocument],
      });
    }
  }, [
    showSnackBar,
    allTemplateFields,
    selectedField,
    structureId,
    updateTemplateField,
    hostTemplateId,
  ]);

  // If fields exist but no selectedField, show loader
  // If no fields exist, continue to render to show add field option
  const hasFields = allTemplateFields && allTemplateFields.length > 0;
  if (hasFields && !selectedField) return <PageLoader />;

  const isTemplateLoading = !hostTemplateId || !hostTemplate || allTemplateFields === undefined;

  return (
    <>
      {showOptionsModal && (
        <BulkOptionsModal
          open={showOptionsModal}
          enableColors={enableColors}
          handleUpdateWithColors={(newOptions, newColors) => {
            if (selectedField) {
              updateField({
                ...selectedField,
                options: [...(selectedField?.options ?? []), ...newOptions],
                displayConfig: selectedField.displayConfig
                  ? {
                      ...selectedField.displayConfig,
                      color: [...(selectedField.displayConfig?.color ?? []), ...newColors],
                    }
                  : selectedField.displayConfig,
              });
            }
          }}
          handleClose={() => {
            setShowOptionsModal(false);
          }}
        />
      )}
      {showDeleteFieldDialog && (
        <GenericDialog
          open={showDeleteFieldDialog}
          onClose={() => setShowDeleteFieldDialog(false)}
          title="Delete Field"
          content={
            <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
              Are you sure you want to delete this field?
            </Typography>
          }
          actions={
            <>
              <Button
                variant="text"
                onClick={() => setShowDeleteFieldDialog(false)}
                sx={{ color: abyssColors.primary[500] }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => handleDeleteField()}
                sx={containedButtonStyle}
              >
                Delete
              </Button>
            </>
          }
          actionsStyles={{ mr: 3, pb: 3 }}
        />
      )}
      {hasFields && (
        <Box sx={{ pt: 1, pb: 2 }}>
          <TextField
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search fields"
            variant="outlined"
            size="small"
            fullWidth
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: !!searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Clear search"
                      onClick={() => setSearchTerm('')}
                      edge="end"
                      size="small"
                    >
                      <CloseIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
      )}

      {isTemplateLoading && <PageLoader />}

      {!isTemplateLoading && filteredFields.length > 0 && (
        <>
          {selectedField ? (
            <Stack>
              <FormHeader>
                {emptyForm ? (
                  <TemplateFields
                    value={''}
                    placeholder="Template Name"
                    mode={emptyForm ? 'Edit' : mode}
                    onChange={(value) => {
                      setSelectedField({ ...selectedField, name: value });
                    }}
                  />
                ) : (
                  <Typography fontSize="2.2rem" fontWeight={500}>
                    {selectedField.name}
                  </Typography>
                )}

                {mode === 'Edit' || emptyForm ? (
                  <Stack direction="row" spacing={1} sx={{ pb: 0.5 }}>
                    <Button
                      variant="contained"
                      sx={{ ...containedButtonStyle, fontSize: '12px', py: 1 }}
                      onClick={() => {
                        if (emptyForm) {
                          handleEmptyForm(false);
                        } else {
                          setMode('ReadOnly');
                          handleSaveTemplate();
                        }
                      }}
                    >
                      Save
                    </Button>
                    <Button
                      variant="contained"
                      sx={{ ...containedButtonStyle, fontSize: '12px', py: 1 }}
                      onClick={() => {
                        setMode('ReadOnly');
                        handleEmptyForm(false);
                      }}
                    >
                      Cancel
                    </Button>
                  </Stack>
                ) : (
                  <Stack direction="row" spacing={0} sx={{ pb: 0.5 }}>
                    <Tooltip
                      title="Edit Template"
                      placement="top"
                      arrow
                      slotProps={{
                        popper: {
                          sx: { tooltipStyles },
                        },
                      }}
                    >
                      <IconButton size="medium" onClick={() => setMode('Edit')}>
                        <Edit
                          fontSize="inherit"
                          sx={{ cursor: 'pointer', color: abyssColors.primary[500] }}
                        />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                )}
              </FormHeader>

              <LineBreak marginBottom={2} />
              <Stack direction="row" width="100%" gap={2}>
                <FieldsList>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={filteredFields.map((f) => f.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <Stack gap={1}>
                        {filteredFields.map((field) => (
                          <SortableField
                            key={field.id}
                            field={field}
                            isSelected={selectedField?.id === field.id}
                            isDragDisabled={isDragDisabled}
                            onClick={() => {
                              const fieldToSelect = field.displayConfig
                                ? field
                                : { ...field, displayConfig: undefined };
                              setSelectedField(fieldToSelect);
                              userSelectedFieldRef.current = fieldToSelect;
                              setEnableColors(
                                !!field?.displayConfig?.color &&
                                  field?.displayConfig?.color.length > 0
                              );
                            }}
                          />
                        ))}
                      </Stack>
                    </SortableContext>
                  </DndContext>
                  {(mode === 'Edit' || emptyForm) && (
                    <Button
                      sx={{ marginTop: 2, height: '40px' }}
                      startIcon={<Add />}
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        const newFieldId = `${NEW_FIELD_PREFIX}${Date.now()}`;
                        const newField = {
                          name: '',
                          key: '',
                          options: [],
                          type: TemplateFieldTypeEnum.Input,
                          unit: null,
                          isReadOnly: true,
                          isRequired: true,
                          serverSide: true,
                          id: newFieldId,
                          displayConfig: {
                            id: generateMongoObjectId(),
                            type: TableCellStyleEnum.Text,
                            isSortable: false,
                            sortType: SortTypeEnum.String,
                            textAreaRows: undefined,
                            textFormat: TextFormatEnum.None,
                          },
                        };
                        setAllTemplateFields([...(allTemplateFields ?? []), newField]);
                        setSelectedField(newField);
                      }}
                    >
                      Add Field
                    </Button>
                  )}
                </FieldsList>
                {selectedField && (
                  <FieldPanel gap={3} direction="row">
                    <Stack width={'50%'} gap={1}>
                      <Grid container maxWidth="60rem" ml={'-1.5rem'}>
                        <Grid size={6}>
                          <Stack direction="row" alignItems="center">
                            <Checkbox
                              checked={selectedField.displayInLogAggregation ?? false}
                              disabled={mode === 'ReadOnly' && !emptyForm}
                              onChange={(event) => {
                                updateField({
                                  ...selectedField,
                                  displayInLogAggregation: event.target.checked,
                                });
                              }}
                            />
                            <Typography>Display in Platform Table</Typography>
                          </Stack>
                        </Grid>
                        <Grid size={6}>
                          <Stack direction="row" alignItems="center">
                            <Checkbox
                              checked={selectedField.isDerived ?? false}
                              disabled={mode === 'ReadOnly' && !emptyForm}
                              onChange={(event) => {
                                updateField({
                                  ...selectedField,
                                  isDerived: event.target.checked,
                                  derivedEquationKey: event.target.checked
                                    ? selectedField.derivedEquationKey
                                    : null,
                                });
                              }}
                            />
                            <Typography>Is Derived Field?</Typography>
                          </Stack>
                        </Grid>
                      </Grid>
                      {(mode === 'Edit' || emptyForm) && (
                        <TemplateFields
                          label="Name"
                          value={selectedField.name}
                          mode={emptyForm ? 'Edit' : mode}
                          onChange={(value) => {
                            const isNewField = selectedField.id.startsWith(NEW_FIELD_PREFIX);
                            const derivedKey = value ? generateFieldKeyFromName(value) : '';
                            updateField({
                              ...selectedField,
                              name: value,
                              ...(isNewField && derivedKey ? { key: derivedKey } : undefined),
                            });
                          }}
                        />
                      )}

                      <Stack spacing={1}>
                        <TemplateFields
                          label="Field Description"
                          value={
                            mode === 'ReadOnly' && !emptyForm && !selectedField.fieldDescription
                              ? 'N/A'
                              : selectedField.fieldDescription || ''
                          }
                          mode={mode === 'ReadOnly' && !emptyForm ? 'ReadOnly' : 'Edit'}
                          onChange={(value) => {
                            updateField({
                              ...selectedField,
                              fieldDescription: value ?? '',
                            });
                          }}
                          type={TemplateFieldTypeEnum.Textarea}
                          placeholder="Enter field description (optional)"
                        />
                        {(mode === 'Edit' || emptyForm) && (
                          <Typography
                            variant="body2"
                            color={abyssColors.primary[500]}
                            component="div"
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1,
                              backgroundColor: abyssColors.primary[100],
                              border: `1px solid ${abyssColors.primary[300]}`,
                              borderRadius: 1,
                              p: 1,
                            }}
                          >
                            <InfoOutlined sx={{ mt: '2px', flexShrink: 0 }} fontSize="small" />
                            <span>
                              Use field description as label for comments fields, e.g. Daily Brief
                            </span>
                          </Typography>
                        )}
                        {(mode === 'Edit' || emptyForm) && (
                          <Typography
                            variant="caption"
                            color={
                              (selectedField.fieldDescription || '').length > 100
                                ? 'error'
                                : 'text.secondary'
                            }
                            sx={{ fontSize: '0.75rem', ml: 2 }}
                          >
                            {(selectedField.fieldDescription || '').length > 100
                              ? `Description is too long (${
                                  (selectedField.fieldDescription || '').length
                                }/100 characters)`
                              : `${(selectedField.fieldDescription || '').length}/100 characters`}
                          </Typography>
                        )}
                      </Stack>

                      <Stack direction="row" alignItems="center">
                        <DropDownContainer>
                          <Typography>Derived Equation Key</Typography>
                          {selectedField.isDerived ? (
                            <>
                              <DropDownField
                                mode={emptyForm ? 'Edit' : mode}
                                id={selectedField.id}
                                title=""
                                sx={dropDownStyles}
                                disableClearable
                                value={
                                  selectedField.derivedEquationKey
                                    ? availableEquations.find(
                                        (equation) =>
                                          equation.key === selectedField.derivedEquationKey
                                      )?.name || selectedField.derivedEquationKey
                                    : ''
                                }
                                options={availableEquations.map((equation) => equation.name)}
                                onFieldValueChange={(value) => {
                                  if (value && selectedField) {
                                    const matchedEquation = availableEquations.find(
                                      (equation) => equation.name === value
                                    );
                                    if (matchedEquation) {
                                      updateField({
                                        ...selectedField,
                                        derivedEquationKey: matchedEquation.key,
                                      });
                                    }
                                  }
                                }}
                              />
                              <Typography
                                variant="caption"
                                sx={{
                                  color: selectedField.derivedEquationKey ? '#666' : '#d32f2f',
                                  fontSize: '0.75rem',
                                  marginTop: '4px',
                                }}
                              >
                                {selectedField.derivedEquationKey
                                  ? 'Equation key selected for this derived field'
                                  : 'Equation key is required for derived fields'}
                              </Typography>
                            </>
                          ) : (
                            <Typography>N/A</Typography>
                          )}
                        </DropDownContainer>
                      </Stack>

                      <TemplateFields label="Key" value={selectedField.key} mode="ReadOnly" />

                      <Stack spacing={1}>
                        <DropDownContainer>
                          <Typography>Field Type</Typography>
                          <DropDownField
                            mode={emptyForm ? 'Edit' : mode}
                            id={selectedField.id}
                            title=""
                            sx={dropDownStyles}
                            disableClearable
                            value={formatEnumForDisplay(selectedField.type) || ''}
                            options={Object.values(TemplateFieldTypeEnum).map((value) =>
                              formatEnumForDisplay(value)
                            )}
                            onFieldValueChange={(value) => {
                              if (value && selectedField) {
                                const type =
                                  Object.values(TemplateFieldTypeEnum).find(
                                    (enumValue) => enumValue === upperSnakeCase(value)
                                  ) ?? TemplateFieldTypeEnum.Input;
                                updateField({
                                  ...selectedField,
                                  type: type,
                                });
                              }
                            }}
                          />
                        </DropDownContainer>
                        {(mode === 'Edit' || emptyForm) && (
                          <Typography
                            variant="body2"
                            color={abyssColors.primary[500]}
                            component="div"
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1,
                              backgroundColor: abyssColors.primary[100],
                              border: `1px solid ${abyssColors.primary[300]}`,
                              borderRadius: 1,
                              p: 1,
                            }}
                          >
                            <InfoOutlined sx={{ mt: '2px', flexShrink: 0 }} fontSize="small" />
                            <span>Select Textarea for comments, e.g. Daily Brief</span>
                          </Typography>
                        )}
                      </Stack>
                      {selectedField.displayConfig?.isSortable && (
                        <Stack
                          direction="row"
                          gap={1}
                          alignItems="center"
                          width="100%"
                          maxWidth="50rem"
                          justifyContent="space-between"
                        >
                          <Typography>Sort Type</Typography>
                          <DropDownField
                            mode={emptyForm ? 'Edit' : mode}
                            id={selectedField.id}
                            title=""
                            sx={dropDownStyles}
                            disableClearable
                            value={formatEnumForDisplay(selectedField.displayConfig.sortType) || ''}
                            options={Object.values(SortTypeEnum).map((value) =>
                              formatEnumForDisplay(value)
                            )}
                            onFieldValueChange={(value) => {
                              if (value) {
                                const sortType = Object.values(SortTypeEnum).find(
                                  (enumValue) => enumValue === upperSnakeCase(value)
                                );
                                updateField({
                                  ...selectedField,
                                  ...(selectedField.displayConfig
                                    ? {
                                        displayConfig: {
                                          ...selectedField.displayConfig,
                                          ...(sortType
                                            ? { sortType }
                                            : { sortType: SortTypeEnum.String }),
                                        },
                                      }
                                    : {
                                        displayConfig: {
                                          id: generateMongoObjectId(),
                                          type: TableCellStyleEnum.Text,
                                          isSortable: false,
                                          ...(sortType
                                            ? { sortType }
                                            : { sortType: SortTypeEnum.String }),
                                          textAreaRows: undefined,
                                        },
                                      }),
                                });
                              }
                            }}
                          />
                        </Stack>
                      )}

                      <Stack
                        direction="row"
                        gap={1}
                        alignItems="center"
                        width="100%"
                        maxWidth="50rem"
                        justifyContent="space-between"
                      >
                        <Typography>Text Format</Typography>
                        <DropDownField
                          mode={emptyForm ? 'Edit' : mode}
                          id={selectedField.id}
                          title=""
                          sx={dropDownStyles}
                          disableClearable
                          value={
                            formatEnumForDisplay(selectedField.displayConfig?.textFormat) || ''
                          }
                          options={Object.values(TextFormatEnum).map((value) =>
                            formatEnumForDisplay(value)
                          )}
                          onFieldValueChange={(value) => {
                            if (value) {
                              const textFormat = Object.values(TextFormatEnum).find(
                                (enumValue) => enumValue === upperSnakeCase(value)
                              );
                              updateField({
                                ...selectedField,
                                ...(selectedField.displayConfig
                                  ? {
                                      displayConfig: {
                                        ...selectedField.displayConfig,
                                        ...(textFormat
                                          ? { textFormat }
                                          : { textFormat: TextFormatEnum.None }),
                                      },
                                    }
                                  : {
                                      displayConfig: {
                                        id: generateMongoObjectId(),
                                        type: TableCellStyleEnum.Text,
                                        isSortable: false,
                                        sortType: SortTypeEnum.String,
                                        ...(textFormat
                                          ? { textFormat }
                                          : { textFormat: TextFormatEnum.None }),
                                        textAreaRows: undefined,
                                      },
                                    }),
                              });
                            }
                          }}
                        />
                      </Stack>

                      {selectedField.type === TemplateFieldTypeEnum.Textarea && (
                        <TemplateFields
                          label="Text Area Rows"
                          value={selectedField.displayConfig?.textAreaRows?.toString()}
                          mode={emptyForm ? 'Edit' : mode}
                          type={TemplateFieldTypeEnum.Numeric}
                        />
                      )}
                      <DropDownContainer>
                        <Typography>Display Type</Typography>
                        <DropDownField
                          mode={emptyForm ? 'Edit' : mode}
                          id={selectedField.id}
                          title=""
                          sx={dropDownStyles}
                          disableClearable
                          value={formatEnumForDisplay(selectedField.displayConfig?.type) || ''}
                          options={Object.values(TableCellStyleEnum).map((value) =>
                            formatEnumForDisplay(value)
                          )}
                          onFieldValueChange={(value) => {
                            if (value) {
                              const type = Object.values(TableCellStyleEnum).find(
                                (enumValue) => enumValue === upperSnakeCase(value)
                              );
                              updateField({
                                ...selectedField,
                                ...(selectedField.displayConfig
                                  ? {
                                      displayConfig: {
                                        ...selectedField.displayConfig,
                                        ...(type ? { type } : { type: TableCellStyleEnum.Text }),
                                      },
                                    }
                                  : {
                                      displayConfig: {
                                        id: generateMongoObjectId(),
                                        ...(type ? { type } : { type: TableCellStyleEnum.Text }),
                                        isSortable: false,
                                        sortType: SortTypeEnum.String,
                                        inputAdorment: undefined,
                                        textAreaRows: undefined,
                                        textFormat: TextFormatEnum.None,
                                      },
                                    }),
                              });
                            }
                          }}
                        />
                      </DropDownContainer>

                      <DropDownContainer>
                        <Typography>Field Unit</Typography>
                        <DropDownField
                          mode={emptyForm ? 'Edit' : mode}
                          id={`${selectedField.id}-field-unit`}
                          title=""
                          sx={dropDownStyles}
                          value={formatEnumForDisplay(selectedField.fieldUnit) || '-'}
                          options={Object.values(SectionUnitsEnum).map((value) =>
                            formatEnumForDisplay(value)
                          )}
                          onFieldValueChange={(value) => {
                            const fieldUnit = value
                              ? Object.values(SectionUnitsEnum).find(
                                  (enumValue) => enumValue === upperSnakeCase(value)
                                )
                              : null;
                            updateField({
                              ...selectedField,
                              fieldUnit: fieldUnit ?? null,
                            });
                          }}
                        />
                      </DropDownContainer>

                      <TemplateFields
                        label="Round to Decimal"
                        value={selectedField.roundToDecimal?.toString()}
                        onChange={(value) => {
                          updateField({
                            ...selectedField,
                            roundToDecimal: Number(value) || selectedField.roundToDecimal,
                          });
                        }}
                        mode={emptyForm ? 'Edit' : mode}
                      />

                      <Stack direction="row" spacing={2} alignItems="center"></Stack>
                      <LineBreak marginY={4} />
                      {(mode === 'Edit' || emptyForm) && (
                        <Button
                          sx={{ width: '50%', height: '40px' }}
                          startIcon={<Delete />}
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            setShowDeleteFieldDialog(true);
                          }}
                        >
                          Remove Field
                        </Button>
                      )}
                    </Stack>
                    {(selectedField.type === TemplateFieldTypeEnum.MultiSelect ||
                      selectedField.type === TemplateFieldTypeEnum.SingleSelect ||
                      selectedField.type === TemplateFieldTypeEnum.RadioButton) && (
                      <Stack sx={{ width: '50%' }} gap={2}>
                        <Stack direction="row" alignItems="center" gap={1}>
                          <Typography fontWeight={500} fontSize="1.6rem">
                            Options
                          </Typography>
                          <Typography variant="subtitle2">
                            ({selectedField?.options?.length})
                          </Typography>
                        </Stack>
                        <TemplateFieldContainer>
                          {selectedField?.options?.map((option, index) => (
                            <OptionBox key={`${option}-${index}`}>
                              <Typography>{option}</Typography>
                              <Stack direction="row" gap={2}>
                                {enableColors && selectedField.displayConfig?.color && (
                                  <ColorSquare
                                    sx={{
                                      width: '1.6rem',
                                      height: '1.6rem',
                                      border: `1px solid ${abyssColors.neutral[300]}`,
                                    }}
                                    color={
                                      selectedField.displayConfig?.color.find(
                                        (color) => color?.key === option
                                      )?.value ?? 'transparent'
                                    }
                                  />
                                )}
                                {(mode === 'Edit' || emptyForm) &&
                                  selectedField.id.startsWith(NEW_FIELD_PREFIX) && (
                                    <Delete
                                      onClick={() => {
                                        handleRemoveOption(option);
                                      }}
                                    />
                                  )}
                              </Stack>
                            </OptionBox>
                          ))}
                        </TemplateFieldContainer>
                        {(mode === 'Edit' || emptyForm) &&
                          selectedField.id.startsWith(NEW_FIELD_PREFIX) && (
                            <>
                              <Stack
                                direction="row"
                                alignItems="center"
                                gap={1}
                                justifyContent="space-between"
                              >
                                <Stack direction="row" alignItems="center" gap={1}>
                                  <Checkbox
                                    disableRipple
                                    sx={{ padding: 0 }}
                                    checked={enableColors ?? false}
                                    disabled={!selectedField.id.startsWith(NEW_FIELD_PREFIX)}
                                    onChange={(event) => {
                                      setEnableColors(event.target.checked);
                                      updateField({
                                        ...selectedField,
                                        ...(selectedField.displayConfig
                                          ? {
                                              displayConfig: {
                                                ...selectedField.displayConfig,
                                                color: event.target.checked ? [] : undefined,
                                              },
                                            }
                                          : {
                                              displayConfig: {
                                                id: generateMongoObjectId(),
                                                type: TableCellStyleEnum.Text,
                                                isSortable: false,
                                                sortType: SortTypeEnum.String,
                                                textAreaRows: undefined,
                                                color: event.target.checked ? [] : undefined,
                                              },
                                            }),
                                      });
                                    }}
                                  />
                                  <Typography>Option Colors</Typography>
                                </Stack>
                                <Typography color={abyssColors.neutral[500]}>
                                  Unchecking this will remove existing colors from all options for
                                  this field
                                </Typography>
                              </Stack>
                              <Stack
                                direction="row"
                                gap={2}
                                marginBottom={2}
                                width="100%"
                                alignItems="center"
                              >
                                <Stack width="100%">
                                  <TemplateFields
                                    label="Option Name"
                                    value={newOption}
                                    onChange={(value) => {
                                      if (value) {
                                        setNewOption(value);
                                      }
                                    }}
                                    mode={emptyForm ? 'Edit' : mode}
                                  />
                                  {enableColors && (
                                    <Stack direction="row" alignItems="center" gap={1}>
                                      <TemplateFields
                                        label="Option Color"
                                        value={newOptionColor}
                                        onChange={(value) => {
                                          if (value) {
                                            setNewOptionColor(value);
                                          }
                                        }}
                                        mode={emptyForm ? 'Edit' : mode}
                                        endAdornment="color"
                                      />
                                    </Stack>
                                  )}
                                </Stack>
                                <Button
                                  sx={{ width: '20rem', height: '32px' }}
                                  startIcon={<AddCircle />}
                                  variant="outlined"
                                  size="small"
                                  disabled={!newOption}
                                  onClick={() => {
                                    if (newOption) {
                                      updateField({
                                        ...selectedField,
                                        options: [...(selectedField?.options ?? []), newOption],
                                        displayConfig:
                                          selectedField.displayConfig && newOptionColor
                                            ? {
                                                ...selectedField.displayConfig,
                                                color: [
                                                  ...(selectedField.displayConfig?.color ?? []),
                                                  {
                                                    key: newOption,
                                                    value: newOptionColor,
                                                  },
                                                ],
                                              }
                                            : selectedField.displayConfig,
                                      });
                                    }
                                    setNewOption('');
                                    setNewOptionColor('');
                                  }}
                                >
                                  Add New Option
                                </Button>
                              </Stack>
                              <Button
                                sx={{ width: '50%', height: '40px', ml: 'auto' }}
                                startIcon={<CloudUpload />}
                                variant="outlined"
                                size="small"
                                onClick={() => {
                                  setShowOptionsModal(true);
                                }}
                              >
                                Add options from file
                              </Button>
                            </>
                          )}
                        {(mode === 'Edit' || emptyForm) &&
                          !selectedField.id.startsWith(NEW_FIELD_PREFIX) && (
                            <Typography textAlign="center" mt={2} color={abyssColors.neutral[400]}>
                              Editing options for existing fields is not allowed
                            </Typography>
                          )}
                        <Typography color={abyssColors.neutral[500]}>
                          <Typography fontWeight={600}>Note:</Typography> To enable filtering, the
                          field must be made editable, as the data is not yet present on the
                          assemblies.
                        </Typography>
                      </Stack>
                    )}
                  </FieldPanel>
                )}
              </Stack>
            </Stack>
          ) : (
            <PageLoader />
          )}
        </>
      )}

      {!isTemplateLoading && filteredFields.length === 0 && !hasFields && (
        <Box sx={{ pt: 2 }}>
          <FieldPanel justifyContent="center" alignItems="center" sx={{ width: '100%' }}>
            <Stack spacing={3} alignItems="center">
              <EmptyStatePlaceholder type="template" message="No Fields Found" />
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Get started by creating your first field
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => {
                  const newFieldId = `${NEW_FIELD_PREFIX}${Date.now()}`;
                  const newField = {
                    name: '',
                    key: '',
                    options: [],
                    type: TemplateFieldTypeEnum.Input,
                    unit: null,
                    isReadOnly: true,
                    isRequired: true,
                    serverSide: true,
                    id: newFieldId,
                    displayConfig: {
                      id: generateMongoObjectId(),
                      type: TableCellStyleEnum.Text,
                      isSortable: false,
                      sortType: SortTypeEnum.String,
                      textAreaRows: undefined,
                      textFormat: TextFormatEnum.None,
                    },
                  };
                  setAllTemplateFields([...(allTemplateFields ?? []), newField]);
                  setSelectedField(newField);
                }}
                sx={containedButtonStyle}
              >
                Add First Field
              </Button>
            </Stack>
          </FieldPanel>
        </Box>
      )}

      {!isTemplateLoading && filteredFields.length === 0 && hasFields && (
        <Box sx={{ pt: 2 }}>
          <FieldPanel justifyContent="center" alignItems="center" sx={{ width: '100%' }}>
            <EmptyStatePlaceholder type="template" message="No Results Found" />
          </FieldPanel>
        </Box>
      )}
    </>
  );
};
